import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { CustomAlertService } from 'app/modules/shared/services/alert.service';
import { DataSourceService } from '../../dataSources.service';
import { Datasource } from '../../datasources';
import { AddDataSourcesComponent } from '../add-data-sources/add-data-sources.component';
import { DeleteConfirmationComponent } from 'app/ui/delete-confirmation/delete-confirmation.component';
import { DataSourceUsageDialogComponent } from './data-source-usage-dialog/data-source-usage-dialog.component';
import {
    dataSourceExternalTablesCommands,
    dataSourceFilesCommands,
    dataSourceOpenCommands,
    dataSourceTablesListCommands,
    isExternalDatatype,
    isMediaContainerDataSource,
} from '../../data-source-table-route.util';

/** Which external connectors the tenant has a working integration for. */
export interface ExternalIntegrationFlags {
    can_use_sap: boolean;
    can_use_snowflake: boolean;
    can_use_oracle: boolean;
    can_use_remote_postgres: boolean;
}

export const NO_EXTERNAL_INTEGRATIONS: ExternalIntegrationFlags = {
    can_use_sap: false,
    can_use_snowflake: false,
    can_use_oracle: false,
    can_use_remote_postgres: false,
};

@Injectable({ providedIn: 'root' })
export class DataSourceActionsService {
    constructor(
        private readonly _router: Router,
        private readonly _dialog: MatDialog,
        private readonly _alert: CustomAlertService,
        private readonly _dataSourceService: DataSourceService
    ) { }

    private readonly _iconMap: Record<string, string> = {
        'Unstructured Documents': 'Documents.png',
        'Structured Tables': 'tables.png',
        'SAP': 'SAP logo.png',
        'Snowflake': 'snowflake.png',
        'Oracle': 'Oracle logo.png',
        'remoteContainer': 'S3.png',
        'AWS_S3': 'S3.png',
        'RDS': 'RDS logo.png',
        'Remote Postgres': 'postgresql.svg'
    };

    getIconFile(datatype: string): string | undefined {
        return this._iconMap[datatype];
    }

    navigateToDetails(ds: any): void {
        const commands = dataSourceOpenCommands(ds);
        if (commands) {
            this._router.navigate(commands);
        }
    }

    navigateToTables(ds: any, isExternal?: boolean | string): void {
        if (!ds) return;

        if (isMediaContainerDataSource(ds)) {
            this._router.navigate(dataSourceFilesCommands(ds.eid ?? ds.id));
            return;
        }
        const { eid, datatype } = ds;
        const external = !!isExternal || isExternalDatatype(datatype);

        if (external) {
            this._router.navigate(dataSourceExternalTablesCommands(eid, datatype));
            return;
        }

        this._router.navigate(dataSourceTablesListCommands(eid));
    }

    navigateToDocuments(ds: any): void {
        if (!ds) return;
        this._router.navigate(['/data-sources/document', ds.eid]);
    }

    /**
     * Opens the add/edit/view data source dialog. Integration availability is passed
     * as a single object so adding a connector does not change the call signature.
     */
    openCreateDialog(
        flags: ExternalIntegrationFlags,
        type?: string,
        dataSource?: Datasource
    ): Observable<boolean> {
        return this._dialog.open(AddDataSourcesComponent, {
            width: 'min(96vw, 1400px)',
            maxHeight: '95dvh',
            panelClass: 'biggDialog',
            autoFocus: false,
            data: {
                type: type ?? 'Add',
                dataSource,
                ...flags,
            },
        })
            .afterClosed()
            .pipe(
                map(result => !!result)
            );
    }

    /**
     * Opens the read-only Usage dialog for a data source. Fetches on open —
     * does not call usage for every card on list load. Does not change delete.
     */
    openUsageDialog(item: { eid?: string | number; id?: string | number; name?: string }): void {
        const eid = item?.eid ?? item?.id;
        if (eid == null || eid === '') {
            return;
        }
        this._dialog.open(DataSourceUsageDialogComponent, {
            autoFocus: false,
            panelClass: ['sai-dialog-xl', 'data-source-usage-dialog-panel'],
            data: {
                eid: String(eid),
                name: String(item?.name ?? ''),
            },
        });
    }

    confirmAndDelete(item: any): Observable<boolean> {
        return this._dialog.open(DeleteConfirmationComponent, {
            width: '30%',
            data: {
                name: item?.name,
                from: 'datasource',
                isMediaContainer: isMediaContainerDataSource(item),
            },
            panelClass: 'cropperDialog',
        })
            .afterClosed()
            .pipe(
                switchMap((confirmed) => {
                    if (!confirmed) {
                        return of(false);
                    }

                    return this._dataSourceService.delete(item.eid).pipe(
                        tap(() => {
                            this._alert.successMessage('Datasource has been deleted successfully!!');
                        }),
                        map(() => true),
                        catchError((error) => {
                            this._alert.failedMessage(
                                error?.error?.message || 'Failed to delete datasource.'
                            );
                            return of(false);
                        })
                    );
                })
            );
    }
}
